import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import Link from "next/link";
import { getBlogPagePath } from "~/lib/blogArchive";

type Props = {
  currentPage: number;
  totalPages: number;
};

type PaginationItem = number | "ellipsis";

function getPaginationItems(
  currentPage: number,
  totalPages: number
): PaginationItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: PaginationItem[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    items.push("ellipsis");
  }

  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (end < totalPages - 1) {
    items.push("ellipsis");
  }

  items.push(totalPages);

  return items;
}

const sharedButtonClassName =
  "inline-flex min-w-10 items-center justify-center rounded-lg border border-sky-800 px-3 py-2 text-sm font-medium transition-colors duration-300";

function pageButtonClassName(active: boolean, extra?: string) {
  return clsx(
    sharedButtonClassName,
    active
      ? "bg-sky-800 text-white"
      : "bg-white text-sky-800 hover:bg-sky-800 hover:text-white",
    extra
  );
}

export const BlogPagination: React.FC<Props> = ({
  currentPage,
  totalPages,
}) => {
  if (totalPages < 2) {
    return null;
  }

  const items = getPaginationItems(currentPage, totalPages);

  return (
    <nav
      aria-label="Paginacja poradnika"
      className="mt-10 flex flex-wrap items-center justify-center gap-2"
    >
      {currentPage > 1 ? (
        <Link
          href={getBlogPagePath(currentPage - 1)}
          className={pageButtonClassName(false, "gap-1.5")}
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          <span>Poprzednia</span>
        </Link>
      ) : null}

      {items.map((item, index) =>
        item === "ellipsis" ? (
          <span
            key={`ellipsis-${index}`}
            className="inline-flex min-w-10 items-center justify-center px-1 text-sm text-gray-400"
          >
            ...
          </span>
        ) : (
          <Link
            key={item}
            href={getBlogPagePath(item)}
            aria-current={item === currentPage ? "page" : undefined}
            className={pageButtonClassName(item === currentPage)}
          >
            {item}
          </Link>
        )
      )}

      {currentPage < totalPages ? (
        <Link
          href={getBlogPagePath(currentPage + 1)}
          className={pageButtonClassName(false, "gap-1.5")}
        >
          <span>Następna</span>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      ) : null}
    </nav>
  );
};
